<template>
  <div class="!pt-0">
    <div class="flex flex-col max-h-screen pt-16 pb-2.5 min-h-screen space-y-2.5">
      <div class="flex items-stretch justify-between mt-2.5">
        <div class="element-island">
          <h1 class="text-3xl font-bold text-gray-900">
            {{ $t('HeadContract.CenSchedule.title') }}
          </h1>

          <p class="mt-2 text-sm text-gray-600">
            {{ $t('HeadContract.CenSchedule.subTitle') }}
          </p>
        </div>

        <div
          v-if="project?.unarchived"
          class="element-island flex items-center"
        >
          <button
            type="button"
            class="btn btn--secondary-blue space-x-2 flex items-center"
            @click="showAddCenModal = true"
          >
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-4 h-4">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

            <span>{{ $t('HeadContract.CenSchedule.button.addCen') }}</span>
          </button>
        </div>
      </div>

      <div class="element-island overflow-y-auto flex flex-col flex-1 p-4">
        <CenScheduleTable
          ref="cenScheduleTableRef"
          @open-details="openDetails"
          @open-comment-modal="openCommentModal"
        />
      </div>
    </div>

    <AddCenModal
      v-if="showAddCenModal"
      :project-id="props.projectId"
      @close="showAddCenModal = false"
      @created="onCenCreated"
    />

    <CenShowModal
      v-if="showDetailModal && selectedContractEventId !== undefined"
      :contract-event-id="selectedContractEventId"
      :project-id="props.projectId"
      @close="closeDetails"
      @updated="cenScheduleTableRef?.reload()"
    />

    <CenCommentModal
      v-if="showCommentModal && selectedContractEventId !== undefined"
      :contract-event-id="selectedContractEventId"
      @close="closeCommentModal"
    />
  </div>
</template>

<script setup lang="ts">
import AddCenModal from '@/components/HeadContract/CenSchedule/AddCenModal.vue';
import CenCommentModal from '@/components/HeadContract/CenSchedule/CenCommentModal.vue';
import CenScheduleTable from '@/components/HeadContract/CenSchedule/CenScheduleTable.vue';
import CenShowModal from '@/components/HeadContract/CenSchedule/CenShowModal.vue';
import ContractEvent from '@/models/ContractEvent';
import { useProjectsStore } from '@/store/projects';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useRouter } from 'vue-router';

const props = defineProps<{
  projectId: string;
  cenId?: string;
}>();

const projectsStore = useProjectsStore();
const project = computed(() => projectsStore.project);
const cenScheduleTableRef = useTemplateRef<InstanceType<typeof CenScheduleTable>>('cenScheduleTableRef');
const selectedContractEventId = ref<number | undefined>();
const showDetailModal = ref(false);
const showAddCenModal = ref(false);
const showCommentModal = ref(false);
const router = useRouter();

const onCenCreated = () => {
  cenScheduleTableRef.value?.reload();
};

const openDetails = (event: ContractEvent) => {
  router.replace({ name: 'CenSchedule', params: { projectId: props.projectId, cenId: event.id.toString() } });
  selectedContractEventId.value = event.id;
  showDetailModal.value = true;
};

const closeDetails = () => {
  router.replace({ name: 'CenSchedule', params: { projectId: props.projectId } });
  showDetailModal.value = false;
  selectedContractEventId.value = undefined;
};

const openCommentModal = (event: ContractEvent) => {
  selectedContractEventId.value = event.id;
  showCommentModal.value = true;
};

const closeCommentModal = () => {
  showCommentModal.value = false;
  selectedContractEventId.value = undefined;
};

watch(() => props.cenId, (newValue) => {
  if(newValue) {
    selectedContractEventId.value = parseInt(newValue);
    showDetailModal.value = true;
  }
}, { immediate: true });
</script>
